import { PayoutBatchItemStatus, type PayoutBatchSelectionOp } from "../types/payoutBatches.types";

export type SelectionDirection = "select" | "deselect";

export interface SelectionState {
  /** What the person wants each toggled row to be, until the server confirms it. */
  overlay: Record<string, boolean>;
  /** Toggles not yet sent, coalesced per row. */
  pending: Record<string, SelectionDirection>;
  /** Toggles sent in the PATCH currently in flight. */
  inFlight: Record<string, SelectionDirection> | null;
}

export type SelectionAction =
  | { type: "toggle"; id: string; selected: boolean; serverSelected: boolean }
  | { type: "flush" }
  | { type: "settled" }
  | { type: "rollback" }
  | { type: "reset" };

export const INITIAL_SELECTION_STATE: SelectionState = {
  overlay: {},
  pending: {},
  inFlight: null,
};

const without = <T>(record: Record<string, T>, id: string): Record<string, T> => {
  const next = { ...record };
  delete next[id];
  return next;
};

/**
 * Optimistic row selection. A toggle back to what the server already has cancels the pending op instead of
 * queueing two; `flush` moves the pending toggles into one in-flight PATCH; `settled` clears them (the server
 * now agrees with the overlay) and `rollback` forgets them (the server state stands). Toggles made while a
 * PATCH is in flight wait for the next flush.
 */
export function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case "toggle": {
      if (action.selected === action.serverSelected && !state.inFlight?.[action.id]) {
        return {
          ...state,
          overlay: without(state.overlay, action.id),
          pending: without(state.pending, action.id),
        };
      }
      return {
        ...state,
        overlay: { ...state.overlay, [action.id]: action.selected },
        pending: {
          ...state.pending,
          [action.id]: action.selected ? "select" : "deselect",
        },
      };
    }
    case "flush": {
      if (state.inFlight || Object.keys(state.pending).length === 0) return state;
      return { ...state, pending: {}, inFlight: state.pending };
    }
    case "settled": {
      if (!state.inFlight) return state;
      let overlay = state.overlay;
      for (const id of Object.keys(state.inFlight)) {
        if (!(id in state.pending)) overlay = without(overlay, id);
      }
      return { ...state, overlay, inFlight: null };
    }
    case "rollback": {
      if (!state.inFlight) return state;
      let overlay = state.overlay;
      for (const id of Object.keys(state.inFlight)) {
        if (!(id in state.pending)) overlay = without(overlay, id);
      }
      return { ...state, overlay, inFlight: null };
    }
    case "reset":
      return INITIAL_SELECTION_STATE;
  }
}

export interface SelectionOp {
  op: Extract<PayoutBatchSelectionOp, "select" | "deselect">;
  item_ids: string[];
}

/** The PATCH bodies one flush produces: at most one `select` and one `deselect`. */
export function selectionOps(toggles: Record<string, SelectionDirection>): SelectionOp[] {
  const select: string[] = [];
  const deselect: string[] = [];
  for (const [id, direction] of Object.entries(toggles)) {
    (direction === "select" ? select : deselect).push(id);
  }
  const ops: SelectionOp[] = [];
  if (select.length) ops.push({ op: "select", item_ids: select });
  if (deselect.length) ops.push({ op: "deselect", item_ids: deselect });
  return ops;
}

/** What the server holds for each toggled row once the PATCH succeeded, to patch the page before it re-reads. */
export function settledStatuses(
  toggles: Record<string, SelectionDirection>,
): Record<string, PayoutBatchItemStatus> {
  const statuses: Record<string, PayoutBatchItemStatus> = {};
  for (const [id, direction] of Object.entries(toggles)) {
    statuses[id] = direction === "select" ? PayoutBatchItemStatus.SELECTED : PayoutBatchItemStatus.EXCLUDED;
  }
  return statuses;
}

export const hasPendingSelection = (state: SelectionState): boolean =>
  Object.keys(state.pending).length > 0 || state.inFlight !== null;

/** The row as the person sees it: the overlay wins over the server while a toggle is unconfirmed. */
export const isSelectedWithOverlay = (state: SelectionState, id: string, serverSelected: boolean): boolean =>
  id in state.overlay ? state.overlay[id] : serverSelected;
